// note-mention.ts — find / replace bare term mentions in a note body.
//
// Pure text helpers for the Labyrinth alias flow. A "bare" occurrence is a
// whole-word, case-insensitive match in the BODY only — never the H1 title (the
// note's identity) and never inside an existing [[wiki]] / [md](link) span or a
// code block — so wrapping a term as a link can't corrupt the title, nest markup,
// or touch code.

import { escapeRegExp } from '../utils/escape-regexp.js';

export interface BareHit {
  /** the H1 title (everything before the searched body), unchanged. */
  head: string;
  /** the body that was searched (content minus the title line). */
  body: string;
  /** index of the match within `body`. */
  index: number;
  /** length of the match. */
  length: number;
  /** the matched text, with its original casing. */
  matched: string;
}

/** Split `content` into its H1 title (`head`), the searched `body`, and a
 *  length-preserving `masked` copy of the body with code + link spans blanked —
 *  so a match index into `masked` lines up byte-for-byte with `body`. Shared by the
 *  first/all finders so "bare" means the same thing everywhere. */
export function maskedBody(content: string): { head: string; body: string; masked: string } {
  const nl = content.indexOf('\n');
  const titleLen = nl !== -1 && /^#{1,6}\s/.test(content) ? nl + 1 : 0;
  const head = content.slice(0, titleLen);
  const body = content.slice(titleLen);
  const blank = (m: string): string => ' '.repeat(m.length);
  const masked = body
    .replace(/```[\s\S]*?```/g, blank) // fenced code
    .replace(/`[^`]*`/g, blank) // inline code
    .replace(/\[\[[^\]]*\]\]/g, blank) // wiki links
    .replace(/\[[^\]]*\]\([^)]*\)/g, blank); // markdown links
  return { head, body, masked };
}

/** Cheap presence test: does `term` occur as a whole word in an ALREADY-masked
 *  body (from `maskedBody(content).masked`)? Lets a caller mask a note ONCE and test
 *  many terms against it, instead of re-masking per term (the alias thread walk). */
export function hasBareTerm(masked: string, term: string): boolean {
  return !!term && new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i').test(masked);
}

/** EVERY whole-word, case-insensitive BARE occurrence of `term` in `content` (the
 *  same title-skip + code/link masking as `hasBareTerm`), in document order.
 *  All hits share the same `head`/`body`; powers the vault-wide alias review. */
export function findAllBareTerms(content: string, term: string): BareHit[] {
  if (!term) return [];
  const { head, body, masked } = maskedBody(content);
  const re = new RegExp(`\\b${escapeRegExp(term)}\\b`, 'gi');
  const out: BareHit[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(masked)) !== null) {
    const index = m.index;
    const length = m[0].length;
    out.push({ head, body, index, length, matched: body.slice(index, index + length) });
    if (length === 0) re.lastIndex++; // defensive; \b…\b never matches empty
  }
  return out;
}

/**
 * Replace a CHOSEN set of bare hits (from `findAllBareTerms` on the SAME content)
 * with `replacement`, carrying each match's casing into the replacement's copy of
 * `term`. Hits are spliced right-to-left so earlier indices stay valid; hits not
 * passed are left untouched (so the caller can link some occurrences and skip
 * others — e.g. a second, different "Matt"). Returns `content` unchanged for `[]`.
 */
export function replaceBareHits(
  content: string,
  term: string,
  replacement: string,
  hits: BareHit[]
): string {
  if (!hits.length) return content;
  const head = hits[0].head;
  let body = hits[0].body;
  for (const h of [...hits].sort((a, b) => b.index - a.index)) {
    let rep = replacement;
    const ri = rep.toLowerCase().indexOf(term.toLowerCase());
    if (ri !== -1) rep = rep.slice(0, ri) + h.matched + rep.slice(ri + term.length);
    body = body.slice(0, h.index) + rep + body.slice(h.index + h.length);
  }
  return head + body;
}

/** A single appearance of `term` in a note body — either BARE (a whole-word,
 *  unlinked mention, still a {@link BareHit}) or already LINKED to the chosen
 *  target as `[text](targetHref)`. The Thread alias step lists these so each
 *  occurrence can be linked or unlinked independently, in context. */
export interface TermSpan extends BareHit {
  /** true when this appearance is already a `[text](targetHref)` link. */
  linked: boolean;
}

/** Every appearance of `term` in `content` — BARE mentions (the same title-skip +
 *  code/link masking as {@link findAllBareTerms}) PLUS any already linked to
 *  `targetHref` as `[text](targetHref)` — in document order. With no `targetHref`
 *  (e.g. a Create suggestion before its target is named) only the bare mentions are
 *  returned. Bare spans are safe to pass straight to {@link replaceBareHits}. */
export function findTermSpans(content: string, term: string, targetHref: string): TermSpan[] {
  if (!term) return [];
  const { head, body, masked } = maskedBody(content);
  const spans: TermSpan[] = [];
  const bareRe = new RegExp(`\\b${escapeRegExp(term)}\\b`, 'gi');
  let m: RegExpExecArray | null;
  while ((m = bareRe.exec(masked)) !== null) {
    const i = m.index;
    const len = m[0].length;
    spans.push({
      head,
      body,
      index: i,
      length: len,
      matched: body.slice(i, i + len),
      linked: false,
    });
    if (len === 0) bareRe.lastIndex++; // defensive; \b…\b never matches empty
  }
  if (targetHref) {
    const linkRe = new RegExp(
      `\\[(${escapeRegExp(term)})\\]\\(${escapeRegExp(targetHref)}\\)`,
      'gi'
    );
    while ((m = linkRe.exec(body)) !== null) {
      spans.push({ head, body, index: m.index, length: m[0].length, matched: m[1], linked: true });
    }
  }
  spans.sort((a, b) => a.index - b.index);
  return spans;
}

/** Revert ONE already-linked span (from a CURRENT {@link findTermSpans} scan) back
 *  to its bare visible text — the inverse of linking a single occurrence. The span
 *  carries the `head`/`body` it was scanned from, so only that one link is undone;
 *  every other span in the note is left byte-for-byte untouched. */
export function unlinkSpan(span: TermSpan): string {
  const { head, body, index, length, matched } = span;
  return head + body.slice(0, index) + matched + body.slice(index + length);
}
